// One row per LOGICAL page, out of the rows the listing actually returns.
//
// WHY IT EXISTS
//
// A page is one ROW PER LANGUAGE — `route` and `locale` on each, joined to its
// siblings by a shared `translationKey` — which is the storage model the render
// path wants and is not in question here. What it does to the listing is: a
// site with twenty pages in three languages is sixty rows, the same title three
// times, and the grouping left for the author to do in their head.
//
// So the screen groups. Nothing on the wire changes: `PageFragment` already
// selects `locale` and `translationKey`, the rows are already in the browser,
// and this is a narrowing of what is DRAWN out of what was already fetched —
// the same shape as components/tag-filter, through the same two props on
// @repoflow/components/list-cursor (`filter` and `onData`).
//
// WHAT IT CANNOT KNOW, said once here rather than in the screen
//
// The listing is CURSOR-PAGINATED and a group can span the boundary: the first
// fetch can hold the English and Spanish rows of a page whose German row is on
// the next page. So a group is only ever complete with respect to what has been
// loaded, and `hasMore` below is what lets the screen say so instead of drawing
// a row that reads as the whole truth. Grouping server-side is the answer that
// would not have this limit, and it is an API change — see the note in the
// screen for why it is not this change.
import { useCallback, useMemo, useRef, useState } from "react";
import _get from "lodash/get";
import { localeOf } from "@common/render/page";

// The language a page is written in, resolved the way the RENDERER resolves it:
// `page.locale || site.defaultLocale || "en"`. Imported rather than repeated,
// because a listing that labels a row `es` while `<html lang>` says `en` is a
// screen that disagrees with the service, and a second copy of the fallback
// chain is how that starts.
export { localeOf };

// The site's own default, which is what a page carrying no locale of its own
// resolves to. `localeOf` with no page is exactly that expression.
export const defaultLocaleOf = (site) => localeOf(site, null);

// WHICH ROWS ARE ONE PAGE.
//
// An EMPTY `translationKey` groups NOTHING. It is the default on every page
// written before the field existed and on every page nobody has translated —
// `PageModel.create` stores `translationKey || ""` and `update` collapses an
// absent one back to `""` — so a site's entire history shares it. Grouped on
// the raw value, a hundred untranslated pages would collapse into one row
// joined by nothing but their emptiness. Each such page is its own group, and
// the pageId (unique, and already the row's identity) is what names it.
//
// This is the guard `alternatesOf` makes in api/code/src/render/resolve.js, and
// it is made the same way ON PURPOSE — falsy is ungrouped, and everything else
// is compared as the stored string, untrimmed. A key of " " groups two pages
// there, so it groups them here; a screen that tidied the value first would
// show two unrelated rows for a pair the rendered page links together with
// hreflang.
export const groupKeyOf = (page) => {
  const key = page && page.translationKey;

  return key ? `key:${key}` : `page:${(page && page.pageId) || ""}`;
};

// The order the languages of one page are read in, and — its first element
// being the group's fallback head — an order that must not depend on the order
// the rows were FETCHED in.
//
// The site's declared order, the way the Translations tab orders its columns
// (`columnsOf` in routes/sites/site/view/translations.js): a site chose that
// order and reading its languages in two different orders on two screens is how
// an author stops trusting either. A locale the site does not declare — the
// state a site lands in by dropping a language it has already published in —
// sorts after every declared one rather than vanishing, and the code itself
// breaks the remaining ties.
//
// Plain `<` and not `localeCompare`: this has to be the same order in every
// browser, and ICU collation is not.
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

export const memberOrder = (site) => {
  const declared = new Map(
    ((site && site.locales) || [])
      .map((locale, index) => [(locale && locale.code) || "", index])
      .filter(([code]) => code)
  );

  const rank = (code) => (declared.has(code) ? declared.get(code) : declared.size);

  return (a, b) => {
    const codeA = localeOf(site, a);
    const codeB = localeOf(site, b);

    return (
      compare(rank(codeA), rank(codeB)) ||
      compare(codeA, codeB) ||
      compare((a && a.pageId) || "", (b && b.pageId) || "")
    );
  };
};

// WHICH LANGUAGE HEADS THE ROW.
//
// The site's `defaultLocale`, which is the answer the rest of the service
// already gives: it is what a page with no locale of its own is written in, it
// is what `/` serves, and it is what `x-default` points at. So the title and
// the route on the row are the ones a reader lands on by default.
//
// The fallback — a group with no member in the default locale, which is a page
// that exists only in Spanish and German — is the first of the order above, so
// it is the site's own first declared language and never "whichever row the
// cursor happened to return first".
export const headOf = (members, site) => {
  const code = defaultLocaleOf(site);
  // Ordered HERE rather than trusted from the caller. Reading the fallback off
  // `members[0]` would make the head depend on the order the rows were handed
  // over — which is the order they were fetched in, which is the one thing this
  // must not be. It is also what picks between two members resolving to the
  // same default: a page with no locale and its site's explicit `en` one.
  const ranked = [...(members || [])].sort(memberOrder(site));

  return ranked.find((member) => localeOf(site, member) === code) || ranked[0] || null;
};

/**
 * The rows, as groups.
 *
 * Returns the groups in the order their first-seen row arrived — the listing is
 * sorted by the server and grouping must not re-sort it — plus a lookup from
 * every pageId to the group it belongs to, which is what lets the row's cell
 * name languages the row itself knows nothing about.
 */
export const groupsOf = (rows, site) => {
  const order = memberOrder(site);
  const groups = [];
  const byKey = new Map();
  const byPage = new Map();

  for (const row of rows || []) {
    if (!row || !row.pageId) {
      continue;
    }

    const key = groupKeyOf(row);
    let group = byKey.get(key);

    if (!group) {
      group = { key, members: [], head: null };
      byKey.set(key, group);
      groups.push(group);
    }

    group.members.push(row);
    byPage.set(row.pageId, group);
  }

  for (const group of groups) {
    group.members.sort(order);
    group.head = headOf(group.members, site);
    // The head LEADS, whatever the order above made of it. The two can differ
    // on a site whose default language is not one of its declared ones — the
    // ordinary shape of a site that added `es` and left English implicit, since
    // `defaultLocale` may be empty and `localeOf` resolves that to "en". The
    // row's own language reading second there is the chips disagreeing with the
    // title beside them.
    group.members = [
      group.head,
      ...group.members.filter((member) => member !== group.head),
    ].filter(Boolean);
  }

  return { groups, byPage };
};

/**
 * The state the grouped listing needs, in the shape list-cursor's props want.
 *
 * `filter` keeps one row per group — the head — so the table draws one row per
 * logical page. It is a PURE function of the array it is handed: `filter` is
 * called with (item, index, rows), the accumulated rows are that third
 * argument, and the whole grouping is derived from it. Nothing here depends on
 * a previous render having stored anything, which is what keeps a group from
 * flickering through a wrong shape on the render between a page landing and a
 * `setState` about it committing.
 *
 * `membersOf` is how the head's cell reaches the rest of its group: `content`
 * is handed one item and no way to see the others. The index built during the
 * filter pass is kept on a ref for it — the pass runs while list-cursor renders
 * and the cells render after it, in the same commit, so what the cell reads is
 * the index for the rows being drawn and not the previous one. A pageId that is
 * somehow not in it falls back to the page alone, which is the truth for every
 * group of one anyway.
 *
 * `hasMore` is the SERVER's cursor, reported through `onData`. It is the one
 * thing the screen cannot derive from the rows, and it is what the note above
 * the table is drawn from.
 */
export const usePageGroups = (site, listPath) => {
  const index = useRef({ rows: null, site: null, byPage: new Map() });
  const [hasMore, setHasMore] = useState(false);

  const build = useCallback(
    (rows) => {
      const current = index.current;

      if (current.rows === rows && current.site === site) {
        return current;
      }

      const next = { rows, site, byPage: groupsOf(rows, site).byPage };
      index.current = next;

      return next;
    },
    [site]
  );

  const filter = useCallback(
    (item, _index, rows) => {
      const group = build(rows).byPage.get(item && item.pageId);

      return !group || !group.head || group.head.pageId === item.pageId;
    },
    [build]
  );

  const membersOf = useCallback((page) => {
    const group = index.current.byPage.get(page && page.pageId);

    return group ? group.members : [page];
  }, []);

  const onData = useCallback(
    (rows, data) => {
      // Truthy is "the server has another page", which is the same value
      // list-cursor's own "Load more" is drawn from — so the note and the
      // button appear and disappear together rather than one outliving the
      // other.
      setHasMore(Boolean(_get(data, `${listPath}.last`)));
    },
    [listPath]
  );

  return useMemo(
    () => ({ filter, membersOf, onData, hasMore }),
    [filter, membersOf, onData, hasMore]
  );
};
